import { chartingQueryKeys, invalidateChartingAlerts } from '@/features/charting-next/api/chartingCache';
import {
    createChartingTickerAlert,
    deleteChartingTickerAlert,
    fetchChartingTickerAlerts,
    reportChartingTickerError
} from '@/features/charting-next/api/chartingActivityRepository';
import { fetchChartingFundamental } from '@/features/charting-next/api/chartingInsightsRepository';
import { resolveChartingCapabilities } from '@/features/charting-next/models/chartingCapabilities';
import { useChartingSession } from '@/features/charting-next/services/chartingSession';
import { useRemoteResource } from '@/features/charting-next/services/useRemoteResource';
import { computed, ref, unref, watch } from '@vue/composition-api';

const ALERT_DEFAULT_FIELD = 'valor_actual';
const ALERT_DEFAULT_MODE = '>';
const ERROR_DEFAULT_TYPE = 'wrong_support';

export function useChartingSidebarState(stockRef) {
    const sessionStore = useChartingSession();
    const capabilities = computed(() => resolveChartingCapabilities({ features: sessionStore.features, user: sessionStore.user }));
    const selectedStock = computed(() => unref(stockRef) || null);
    const selectedTicker = computed(() =>
        String(selectedStock.value?.ticker || '')
            .trim()
            .toUpperCase()
    );

    const alertDraft = ref({
        field: ALERT_DEFAULT_FIELD,
        mode: ALERT_DEFAULT_MODE,
        value: ''
    });
    const errorDraft = ref({
        type: ERROR_DEFAULT_TYPE,
        message: ''
    });
    const isSubmittingAlert = ref(false);
    const isSubmittingError = ref(false);

    const fundamentalResource = useRemoteResource({
        key: () => chartingQueryKeys.fundamental(sessionStore.uid, selectedTicker.value),
        loader: () => fetchChartingFundamental(selectedTicker.value),
        ttl: 300000,
        enabled: () => Boolean(selectedTicker.value) && (capabilities.value.canUseFundamental || capabilities.value.canUseDescriptionPanel)
    });
    const alertsResource = useRemoteResource({
        key: () => chartingQueryKeys.alerts(sessionStore.uid, selectedTicker.value),
        loader: () =>
            fetchChartingTickerAlerts({
                uid: sessionStore.uid,
                ticker: selectedTicker.value
            }),
        ttl: 60000,
        enabled: () => sessionStore.isAuthenticated && Boolean(selectedTicker.value) && capabilities.value.canUseAlerts
    });

    const pendingAlerts = computed(() => (alertsResource.data.value || []).filter((item) => !item.isExecuted));
    const executedAlerts = computed(() => (alertsResource.data.value || []).filter((item) => item.isExecuted));

    const setAlertDraftValueFromStock = (field = ALERT_DEFAULT_FIELD) => {
        const stock = selectedStock.value;

        if (!stock) {
            alertDraft.value.value = '';
            return;
        }

        const nextValue = field === 'soport' ? stock.supportPriceValue : field === 'entrada' ? stock.entryPriceValue : field === 'out_price' ? stock.stopPriceValue : field === 'entry_distance' ? stock.entryDistanceValue : stock.currentPriceValue;

        alertDraft.value.value = Number.isFinite(Number(nextValue)) ? String(Number(nextValue).toFixed(2)) : '';
    };

    const selectAlertField = (field) => {
        alertDraft.value.field = field || ALERT_DEFAULT_FIELD;
        setAlertDraftValueFromStock(alertDraft.value.field);
    };

    const createAlert = async () => {
        isSubmittingAlert.value = true;

        try {
            const result = await createChartingTickerAlert({
                uid: sessionStore.uid,
                ticker: selectedTicker.value,
                field: alertDraft.value.field,
                mode: alertDraft.value.mode,
                value: alertDraft.value.value
            });

            await invalidateChartingAlerts(sessionStore.uid, selectedTicker.value);

            return result;
        } finally {
            isSubmittingAlert.value = false;
        }
    };

    const removeAlert = async (alertId) => {
        isSubmittingAlert.value = true;

        try {
            const result = await deleteChartingTickerAlert({
                uid: sessionStore.uid,
                alertId
            });

            await invalidateChartingAlerts(sessionStore.uid, selectedTicker.value);

            return result;
        } finally {
            isSubmittingAlert.value = false;
        }
    };

    const submitErrorReport = async () => {
        isSubmittingError.value = true;

        try {
            const result = await reportChartingTickerError({
                ticker: selectedTicker.value,
                type: errorDraft.value.type,
                message: errorDraft.value.message
            });

            errorDraft.value.message = '';
            errorDraft.value.type = ERROR_DEFAULT_TYPE;

            return result;
        } finally {
            isSubmittingError.value = false;
        }
    };

    watch(
        () => selectedTicker.value,
        () => {
            errorDraft.value.message = '';
            setAlertDraftValueFromStock(alertDraft.value.field);
        },
        {
            immediate: true
        }
    );

    return {
        capabilities,
        selectedStock,
        selectedTicker,
        fundamentalResource,
        alertsResource,
        pendingAlerts,
        executedAlerts,
        alertDraft,
        errorDraft,
        isSubmittingAlert,
        isSubmittingError,
        selectAlertField,
        createAlert,
        removeAlert,
        submitErrorReport
    };
}
